'use client'

import { addBreadcrumb, captureException, metrics, startInactiveSpan } from '@sentry/nextjs'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

type RouteTransitionSource = 'link' | 'history' | 'programmatic'

interface RouteTransitionInput {
  endedAtMs: number
  fromPathname: string
  source: RouteTransitionSource
  startedAtMs: number
  toPathname: string
}

interface RouteTransitionMeasurement {
  durationMs: number
  fromPathname: string
  fromRoute: string
  source: RouteTransitionSource
  toPathname: string
  toRoute: string
  workspaceId: string | undefined
}

interface ActiveRouteTransition {
  fromPathname: string
  source: RouteTransitionSource
  startedAtMs: number
  startMarkName: string
}

type RouteTransitionObserverErrorPhase = 'mark' | 'report'

interface RouteTransitionObserverErrorContext {
  fromPathname: string
  source: RouteTransitionSource
  toPathname: string
}

const ROUTE_TRANSITION_MARK_PREFIX = 'trellis.route_transition'
const ROUTE_TRANSITION_MEASURE_NAME = 'trellis.route_transition.duration'
const Sentry = { captureException }
const PUBLIC_TOKEN_ROUTES = new Set<string>(['manual', 'task'])
const NON_WORKSPACE_ROUTES = new Set<string>([
  '_next',
  'api',
  'auth',
  'favicon.ico',
  'login',
  'manual',
  'task',
  'upsells',
])

function getPathOnly(pathname: string): string {
  const hashIndex = pathname.indexOf('#')
  const withoutHash = hashIndex >= 0 ? pathname.slice(0, hashIndex) : pathname
  const queryIndex = withoutHash.indexOf('?')
  const withoutQuery = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash

  return withoutQuery || '/'
}

function getPathSegments(pathname: string): string[] {
  return getPathOnly(pathname).split('/').filter(Boolean)
}

function normalizedTaskSegment(segments: string[], index: number, segment: string): string | null {
  if (segments[1] === 'list' && index === 2) {
    return ':view'
  }
  if (index === 1 && segment !== 'list' && segment !== 'scheduling') {
    return ':taskId'
  }
  return null
}

function normalizedWorkflowSegment(
  segments: string[],
  index: number,
  segment: string
): string | null {
  if (segments[1] === 'runs' && index === 2) {
    return ':runId'
  }
  if (index === 1 && segment !== 'runs' && segment !== 'templates') {
    return ':workflowId'
  }
  return null
}

function normalizedKnowledgeSegment(segments: string[], index: number): string | null {
  if (index === 1) {
    return ':agentSlug'
  }
  if (segments[2] === 'chat' && index === 3) {
    return ':sessionId'
  }
  return null
}

function normalizedArtifactSegment(index: number): string | null {
  if (index === 1) {
    return ':sessionId'
  }
  return index === 2 ? ':artifactId' : null
}

function normalizeWorkspaceSegment(segments: string[], index: number): string {
  const [section] = segments
  const segment = segments[index]
  const previous = segments[index - 1]

  if ((section === 'inbox' || section === 'inbox-v2') && index === 1) {
    return ':view'
  }
  if ((section === 'inbox' || section === 'inbox-v2') && previous === 'chat') {
    return ':conversationId'
  }

  if (section === 'tasks') {
    const normalized = normalizedTaskSegment(segments, index, segment)
    if (normalized) {
      return normalized
    }
  }

  if (section === 'workflows') {
    const normalized = normalizedWorkflowSegment(segments, index, segment)
    if (normalized) {
      return normalized
    }
  }

  if (section === 'kb') {
    const normalized = normalizedKnowledgeSegment(segments, index)
    if (normalized) {
      return normalized
    }
  }

  if (section === 'agent-artifacts') {
    const normalized = normalizedArtifactSegment(index)
    if (normalized) {
      return normalized
    }
  }

  if (section === 'properties' && index === 1) {
    return ':propertyId'
  }

  return segment
}

export function toRouteTemplate(pathname: string): string {
  const segments = getPathSegments(pathname)
  if (segments.length === 0) {
    return '/'
  }

  const [firstSegment] = segments
  if (PUBLIC_TOKEN_ROUTES.has(firstSegment)) {
    return segments.length > 1 ? `/${firstSegment}/:token` : `/${firstSegment}`
  }

  if (NON_WORKSPACE_ROUTES.has(firstSegment)) {
    return `/${firstSegment}`
  }

  const workspaceSegments = segments.slice(1)
  if (workspaceSegments.length === 0) {
    return '/:workspaceId'
  }

  const normalizedSegments = workspaceSegments.map((_, index) =>
    normalizeWorkspaceSegment(workspaceSegments, index)
  )

  return `/:workspaceId/${normalizedSegments.join('/')}`
}

export function getWorkspaceIdFromPathname(pathname: string): string | undefined {
  const [firstSegment] = getPathSegments(pathname)
  if (!firstSegment || NON_WORKSPACE_ROUTES.has(firstSegment)) {
    return undefined
  }

  return firstSegment
}

export function buildRouteTransitionMeasurement(
  input: RouteTransitionInput
): RouteTransitionMeasurement {
  return {
    fromPathname: input.fromPathname,
    toPathname: input.toPathname,
    fromRoute: toRouteTemplate(input.fromPathname),
    toRoute: toRouteTemplate(input.toPathname),
    workspaceId:
      getWorkspaceIdFromPathname(input.toPathname) ??
      getWorkspaceIdFromPathname(input.fromPathname),
    durationMs: Number(Math.max(0, input.endedAtMs - input.startedAtMs).toFixed(2)),
    source: input.source,
  }
}

function nowMs(): number {
  return performance.now()
}

function makeMarkName(counter: number, state: 'start' | 'end'): string {
  return `${ROUTE_TRANSITION_MARK_PREFIX}.${counter}.${state}`
}

function captureRouteTransitionObserverError(
  error: unknown,
  phase: RouteTransitionObserverErrorPhase,
  context: RouteTransitionObserverErrorContext
): void {
  const workspaceId =
    getWorkspaceIdFromPathname(context.toPathname) ??
    getWorkspaceIdFromPathname(context.fromPathname) ??
    'none'

  Sentry.captureException(error, {
    tags: {
      component: 'RouteTransitionObserver',
      phase,
      source: context.source,
      workspace_id: workspaceId,
    },
    extra: {
      fromRoute: toRouteTemplate(context.fromPathname),
      toRoute: toRouteTemplate(context.toPathname),
    },
  })
}

function getInternalPathnameFromClick(event: MouseEvent): string | undefined {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return undefined
  }

  if (!(event.target instanceof Element)) {
    return undefined
  }

  const anchor = event.target.closest('a')
  if (!(anchor instanceof HTMLAnchorElement)) {
    return undefined
  }
  if (anchor.target && anchor.target !== '_self') {
    return undefined
  }

  const href = anchor.getAttribute('href')
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return undefined
  }

  const url = new URL(href, window.location.href)
  if (url.origin !== window.location.origin) {
    return undefined
  }

  return url.pathname
}

function getInternalPathnameFromHistoryUrl(
  url: string | URL | null | undefined
): string | undefined {
  if (url === undefined || url === null) {
    return undefined
  }

  const nextUrl = new URL(url, window.location.href)
  if (nextUrl.origin !== window.location.origin) {
    return undefined
  }

  return nextUrl.pathname
}

function reportRouteTransition(
  measurement: RouteTransitionMeasurement,
  startedAtMs: number,
  endedAtMs: number
): void {
  const attributes = {
    'trellis.duration_ms': measurement.durationMs,
    'trellis.from_route': measurement.fromRoute,
    'trellis.source': measurement.source,
    'trellis.to_route': measurement.toRoute,
    'trellis.workspace_id': measurement.workspaceId ?? 'none',
  }

  const span = startInactiveSpan({
    name: 'trellis.route_transition',
    op: 'navigation.route',
    startTime: new Date(performance.timeOrigin + startedAtMs),
    attributes,
  })
  span.end(new Date(performance.timeOrigin + endedAtMs))

  metrics.distribution(ROUTE_TRANSITION_MEASURE_NAME, measurement.durationMs, {
    unit: 'millisecond',
    attributes,
  })

  addBreadcrumb({
    category: 'navigation',
    level: 'info',
    message: 'trellis.route_transition',
    data: {
      durationMs: measurement.durationMs,
      fromRoute: measurement.fromRoute,
      source: measurement.source,
      toRoute: measurement.toRoute,
      workspaceId: measurement.workspaceId,
    },
  })
}

function isPathname(value: string | null): value is string {
  return typeof value === 'string' && value.length > 0
}

function isActiveTransition(value: ActiveRouteTransition | null): value is ActiveRouteTransition {
  return value !== null
}

export function RouteTransitionObserver(): null {
  const pathname = usePathname()
  const activeTransitionRef = useRef<ActiveRouteTransition | null>(null)
  const currentPathnameRef = useRef<string | null>(null)
  const previousPathnameRef = useRef<string | null>(null)
  const transitionCounterRef = useRef(0)

  useEffect(() => {
    if (!pathname) {
      return
    }
    currentPathnameRef.current = pathname

    const previousPathname = previousPathnameRef.current
    if (!isPathname(previousPathname)) {
      previousPathnameRef.current = pathname
      return
    }

    if (previousPathname === pathname) {
      return
    }

    const endedAtMs = nowMs()
    const activeTransition = activeTransitionRef.current

    previousPathnameRef.current = pathname
    activeTransitionRef.current = null

    if (!isActiveTransition(activeTransition)) {
      return
    }

    try {
      const endMarkName = makeMarkName(transitionCounterRef.current, 'end')
      performance.mark(endMarkName)
      performance.measure(
        ROUTE_TRANSITION_MEASURE_NAME,
        activeTransition.startMarkName,
        endMarkName
      )

      const measurement = buildRouteTransitionMeasurement({
        fromPathname: activeTransition.fromPathname,
        toPathname: pathname,
        startedAtMs: activeTransition.startedAtMs,
        endedAtMs,
        source: activeTransition.source,
      })

      reportRouteTransition(measurement, activeTransition.startedAtMs, endedAtMs)
      performance.clearMarks(activeTransition.startMarkName)
      performance.clearMarks(endMarkName)
      performance.clearMeasures(ROUTE_TRANSITION_MEASURE_NAME)
    } catch (error) {
      captureRouteTransitionObserverError(error, 'report', {
        fromPathname: activeTransition.fromPathname,
        toPathname: pathname,
        source: activeTransition.source,
      })
      console.warn('[route-transition-observer] failed to report route transition', error)
    }
  }, [pathname])

  useEffect(() => {
    function markTransitionStart(toPathname: string, source: RouteTransitionSource): void {
      const fromPathname = currentPathnameRef.current
      if (!isPathname(fromPathname) || toPathname === fromPathname) {
        return
      }
      if (isActiveTransition(activeTransitionRef.current)) {
        return
      }
      try {
        const nextCounter = transitionCounterRef.current + 1
        const startMarkName = makeMarkName(nextCounter, 'start')
        transitionCounterRef.current = nextCounter
        performance.mark(startMarkName)

        activeTransitionRef.current = {
          fromPathname,
          startedAtMs: nowMs(),
          startMarkName,
          source,
        }
      } catch (error) {
        captureRouteTransitionObserverError(error, 'mark', {
          fromPathname,
          toPathname,
          source,
        })
        console.warn('[route-transition-observer] failed to mark route transition', error)
      }
    }

    function handleClick(event: MouseEvent): void {
      const toPathname = getInternalPathnameFromClick(event)
      if (!toPathname) {
        return
      }

      markTransitionStart(toPathname, 'link')
    }

    function handlePopState(): void {
      markTransitionStart(window.location.pathname, 'history')
    }

    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function pushState(
      data: unknown,
      unused: string,
      url?: string | URL | null
    ): void {
      const toPathname = getInternalPathnameFromHistoryUrl(url)
      if (toPathname) {
        markTransitionStart(toPathname, 'history')
      }
      originalPushState.call(window.history, data, unused, url)
    }

    window.history.replaceState = function replaceState(
      data: unknown,
      unused: string,
      url?: string | URL | null
    ): void {
      const toPathname = getInternalPathnameFromHistoryUrl(url)
      if (toPathname) {
        markTransitionStart(toPathname, 'history')
      }
      originalReplaceState.call(window.history, data, unused, url)
    }

    document.addEventListener('click', handleClick, true)
    window.addEventListener('popstate', handlePopState)

    return () => {
      document.removeEventListener('click', handleClick, true)
      window.removeEventListener('popstate', handlePopState)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [])

  return null
}
